import { datadogLogs } from '@datadog/browser-logs';
import _ from 'lodash';
import { observable, computed, action, makeObservable } from 'mobx';
import Router from 'next/router';
import type { NextRouter } from 'next/router';
import type { RefObject } from 'react';

import { changeNativeTabToShop } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import { guessTimezone } from 'shared/helpers/date-time';
import { ItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { formatAddress, formatAddressWithoutApt, formatAddressForCheckout } from 'shared/helpers/address';
import ROUTES from 'src/routes';
import type { ErnieTypes } from 'shared/constants';
import type { Dispensaries } from 'src/gql/graphql';
import { InterfaceVariants } from '../constants/interface-variants';

type JsonValue = JsonValue[] | boolean | number | string | { [key: string]: JsonValue } | null;

// Hardening for the embedded iframe auto-resizer. If a child page measures
// itself with viewport-relative units, parent-driven resizes can feed back
// into bigger child measurements, growing the iframe unboundedly. These
// caps freeze further resize dispatches once we detect a runaway pattern.
const MAX_IFRAME_HEIGHT_PX = 200000;
const ENABLE_MAX_IFRAME_HEIGHT = false;
const RESIZE_BURST_WINDOW_MS = 1000;
const RESIZE_BURST_THRESHOLD = 30;

type ErnieQueueItem = {
  msg: string;
  type: ErnieTypes;
  timeout: number;
};

export type CurrentAddress = {
  id?: string;
  lat?: number | null;
  lng?: number | null;
  ln1?: string | null;
  ln2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
  address?: string;
  addressLong?: string;
  apt?: string;
  residentialStatus?: string;
  geometry?: { coordinates?: [number, number] };
};

type ProductPosition = {
  productId: string;
  productIndex: number;
  sectionIndex?: number;
};

type ProductDemand = { seenIds: Set<string>; notSeenIds: Set<string> };

type PromiseModal = { open: boolean; resolve?: () => void; reject?: () => void };
type PromiseConfirmationModal = PromiseModal & { confirmed?: boolean };
type UpdateAddressProps = {
  location: CurrentAddress;
  residentialStatus?: string;
};

// store global ephemeral state values such as modal open/close
export default class UIStore {
  // router info copied over for easy access / mobx memoization
  @observable href = '';
  @observable route = '';

  // if we're in an iframe or not, but ssr safe
  @observable isIframe = false;
  @observable isIframeSDK = false;

  // global modal open fo shodal
  @observable viewingGuestOrderSuccess = false;
  @observable showClosedModal = false;
  @observable showMobileFilters = false;
  @observable showPausedOrdersModal = false;
  @observable showKioskCheckoutModal = false;
  @observable showKioskOrderSuccess = false;
  @observable showReinventedModal = false;
  @observable showLocationSettingsDropdown = false;
  @observable showAlpineIQTermsModal = false;
  @observable showCartDrawer = false;
  @observable showCartInterstitial = false;
  @observable cartButtonRef: RefObject<HTMLButtonElement> | null = null;

  // These are interstitial modals, will have passed promise props when open {resolve, reject}
  @observable showResidentialVerificationModal: PromiseModal = { open: false };
  @observable showChangeEmailPasswordPrompt: PromiseModal = { open: false };
  @observable showSearchInput = false;
  @observable menuTypeSwitchWarningObj: PromiseModal = { open: false };
  @observable currentMenuDrawerTab = 1;
  @observable showOutsideDeliveryAreaModal: PromiseModal = { open: false };

  // Mobile drawers
  @observable showOrderTypeDrawer = false;
  @observable showStoreTypeDrawer = false;
  @observable showDispensariesFiltersDrawer = false;
  @observable showMarketplaceMobileSearchDrawer = false;

  // Added for compatibility to help with surface control in v3
  @observable isGlobalHeaderInView = false;
  @observable isDispensaryHeaderInView = true;
  @observable previewMode = false;
  @observable viewOnlyMode = false;
  @observable lastDispensary: Dispensaries | null = null;
  @observable embeddedCName: string | null = null;

  // checkout stuff
  @observable phoneNumberConfirmationObj: PromiseConfirmationModal = { open: false };
  @observable tempAddress = '';
  @observable apt = '';
  @observable timezone: string = guessTimezone();

  // address is *ONLY* filled in while in the checkout view
  // use cart.address instead
  @observable currentAddress: CurrentAddress | null = null;
  @observable loadingOrderType = false;

  // toggle for category menu layout.
  @observable menuLayout = 'list';

  @observable ernieQueue: ErnieQueueItem[] = [];
  @observable isScrolling = false;
  @observable externalReferrer = false;
  @observable isUsingExternalClickHandlerLogin = false;

  @observable iframeParentUrl: string | null = null;
  @observable iframeParentHref: string | null = null;
  @observable iframeHeight: number | null = null;
  @observable iframeHeightOverride = false;
  @observable showEmptySearchWhiteSpace = false;
  @observable previousSearchTerm: string | null = null;
  @observable shouldRestorePreviousScroll = false;
  @observable subheaderIsSticking = false;
  @observable productCount: number = getPersistedValue(`productCount`, 0);
  // positioning data for amplitude
  @observable productPosition: ProductPosition | null = null;
  // For managing the source of adding to cart on product detail
  @observable activeProductSource: ItemListName | null = null;

  // For managing the product demand counts on cart items
  @observable productDemand: ProductDemand = {
    seenIds: new Set(),
    notSeenIds: new Set(),
  };

  // dutchiePay
  @observable showDutchiePaySignupModal = false;
  @observable showDutchiePayInstoreTaxInfo = false;
  @observable showDutchiePayEditAddressModal = false;
  @observable showDutchiePayLeaveModal = false;
  @observable showDutchiePayLearnMoreModal = false;
  @observable showDutchiePayCtaModal = false;
  @observable showDutchiePayQrCodeInKiosk = false;
  @observable showKioskSelectPaymentModal = false;
  @observable showDutchiePayKioskOrderSuccess = false;

  // Retailer Mobile App
  @observable isMobileEcommAppNotificationsEnabled = false;

  // Dutchie Plus - isolatedCheckout
  @observable hideLogin = false;

  // global first render detection
  @observable isFirstRender = true;

  // Kiosk Pro Login Modal
  @observable isKioskProLoginModalOpen = false;

  // Enterprise enrollment dispensary for theming
  @observable enterpriseDispensary: Dispensaries | null = null;

  // Always undefined; see showResidentialStatusModal
  residentialStatus?: string;

  tempLocation: CurrentAddress | null = null;

  // Hardening state for the embedded iframe auto-resizer (see embeddedEventDispatcher).
  resizeDispatchTimestamps: number[] = [];
  resizeDispatchFrozen = false;

  constructor(router: Pick<NextRouter, 'asPath' | 'route'>) {
    makeObservable(this);
    const { asPath, route } = router;

    this.setPath(asPath, route);
  }

  @action.bound setIsKioskProLoginModalOpen(value: boolean): void {
    this.isKioskProLoginModalOpen = value;
  }

  @action.bound setEnterpriseDispensary(dispensary: Dispensaries | null): void {
    this.enterpriseDispensary = dispensary;
  }

  @action.bound setIsMobileEcommAppNotificationsEnabled(value: boolean): void {
    if (this.isMobileEcommApp) {
      this.isMobileEcommAppNotificationsEnabled = value;
    }
  }

  @action.bound openDutchiePaySignUpModal(): void {
    this.showDutchiePaySignupModal = true;
  }

  @action.bound openDutchiePayEditAddressModal(): void {
    this.showDutchiePaySignupModal = true;
    this.showDutchiePayEditAddressModal = true;
  }

  @action.bound updateShowDutchiePayQrCodeInKiosk = (value: boolean): void => {
    this.showDutchiePayQrCodeInKiosk = value;
  };

  @action.bound updateShowKioskSelectPaymentModal = (value: boolean): void => {
    this.showKioskSelectPaymentModal = value;
  };

  @action.bound updateShowDutchiePayKioskOrderSuccess = (value: boolean): void => {
    if (value) {
      this.showDutchiePayQrCodeInKiosk = false;
    }
    this.showDutchiePayKioskOrderSuccess = value;
  };

  @action.bound closeDutchiePaySignUpModal(): void {
    this.showDutchiePaySignupModal = false;
    this.showDutchiePayEditAddressModal = false;
  }

  @action.bound openDutchiePayLeaveModal(): void {
    this.showDutchiePayLeaveModal = true;
  }

  @action.bound closeDutchiePayLeaveModal(): void {
    this.showDutchiePayLeaveModal = false;
  }

  @action.bound openDutchiePayLearnMoreModal(): void {
    this.showDutchiePayLearnMoreModal = true;
  }

  @action.bound closeDutchiePayLearnMoreModal(): void {
    this.showDutchiePayLearnMoreModal = false;
  }

  @action.bound openDutchiePayCtaModal(): void {
    this.showDutchiePayCtaModal = true;
  }

  @action.bound closeDutchiePayCtaModal(): void {
    this.showDutchiePayCtaModal = false;
  }

  @action.bound openDutchiePayInstoreTaxInfo(): void {
    this.showDutchiePayInstoreTaxInfo = true;
  }

  @action.bound closeDutchiePayInstoreTaxInfo(): void {
    this.showDutchiePayInstoreTaxInfo = false;
  }

  @action.bound
  openOrderTypeModal(viewingModalName: 'viewingClosedModal' | 'viewingPausedOrdersModal' = 'viewingClosedModal'): void {
    const keyName = {
      viewingClosedModal: `showClosedModal`,
      viewingPausedOrdersModal: `showPausedOrdersModal`,
    }[viewingModalName];

    if (keyName) {
      this[keyName] = true;
    }
  }

  @action.bound
  setEmbeddedCName(cName: string[] | string): void {
    this.embeddedCName = Array.isArray(cName) ? cName[0] : cName;
  }

  @action.bound
  openPhoneNumberConfirmationModal({ confirmed }: { confirmed: boolean }): Promise<void> {
    return new Promise((resolve) => {
      this.phoneNumberConfirmationObj = { open: true, confirmed, resolve };
    });
  }

  @action
  openPausedOrdersModal(): void {
    this.showPausedOrdersModal = true;
  }

  @action.bound
  toggleLocationSettingsDropdown(): void {
    this.showLocationSettingsDropdown = !this.showLocationSettingsDropdown;
  }

  @action.bound
  openChangeEmailPasswordPrompt(): Promise<void> {
    return new Promise((resolve) => {
      this.showChangeEmailPasswordPrompt = { open: true, resolve };
    });
  }

  @action.bound
  openCartDrawer(): void {
    this.showCartDrawer = true;
  }

  @action.bound
  closeCartDrawer(): void {
    this.showCartDrawer = false;
  }

  @action.bound
  openCartInterstitial(): void {
    this.showCartInterstitial = true;
  }

  @action.bound
  closeCartInterstitial(): void {
    this.showCartInterstitial = false;
  }

  @action.bound
  setCartButtonRef(ref: RefObject<HTMLButtonElement>): void {
    this.cartButtonRef = ref;
  }

  @action.bound
  clearCartButtonRef(): void {
    this.cartButtonRef = null;
  }

  @action.bound
  openOrderTypeDrawer(): void {
    this.showOrderTypeDrawer = true;
  }

  @action.bound
  closeOrderTypeDrawer(): void {
    this.showOrderTypeDrawer = false;
  }

  @action.bound
  openStoreTypeDrawer(): void {
    this.showStoreTypeDrawer = true;
  }

  @action.bound
  closeStoreTypeDrawer(): void {
    this.showStoreTypeDrawer = false;
  }

  @action.bound
  openDispensariesFiltersDrawer(): void {
    this.showDispensariesFiltersDrawer = true;
  }

  @action.bound
  closeDispensariesFiltersDrawer(): void {
    this.showDispensariesFiltersDrawer = false;
  }

  @action.bound
  openMarketplaceMobileSearchDrawer(): void {
    this.showMarketplaceMobileSearchDrawer = true;
  }

  @action.bound
  closeMarketplaceMobileSearchDrawer(): void {
    this.showMarketplaceMobileSearchDrawer = false;
  }

  @action.bound
  openMenuSwitchWarningModal(): Promise<void> {
    return new Promise((resolve) => {
      this.menuTypeSwitchWarningObj = { open: true, resolve };
    });
  }

  @action.bound
  openShowOutsideDeliveryAreaModal(): Promise<void> {
    return new Promise((resolve) => {
      this.showOutsideDeliveryAreaModal = { open: true, resolve };
    });
  }

  @action
  setGlobalHeaderInView(inView: boolean): void {
    this.isGlobalHeaderInView = inView;
  }

  @action
  setDispensaryHeaderInView(inView: boolean): void {
    this.isDispensaryHeaderInView = inView;
  }

  @action
  setShouldRestorePreviousScroll(shouldRestore: boolean): void {
    this.shouldRestorePreviousScroll = shouldRestore;
  }

  @action
  setSubheaderIsSticking(isSticking: boolean): void {
    this.subheaderIsSticking = isSticking;
  }

  @action showErnie = (
    msg = 'Something went wrong. Please contact support.',
    type: ErnieTypes = `danger`,
    timeout?: number
  ): void => {
    const numWords = _.words(msg).length;
    const time = Math.round((60 / 150) * numWords * 1000 + 300); // assuming 150 wpm and reaction time of 0.3 sec
    // explanation: (60 sec / 150 words) * numWords * (1000 millisecs / 1 sec) + 0.3 sec
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    this.ernieQueue.push({ msg, type, timeout: timeout || time });
  };

  shouldDispatchResize = (height: unknown): boolean => {
    if (this.resizeDispatchFrozen) {
      return false;
    }
    if (typeof height !== 'number' || !Number.isFinite(height) || height < 0) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- gated behind feature constant
    if (ENABLE_MAX_IFRAME_HEIGHT && height > MAX_IFRAME_HEIGHT_PX) {
      this.resizeDispatchFrozen = true;
      datadogLogs.logger.warn('Embedded iframe resize dispatch frozen: height exceeds max', {
        height,
        max: MAX_IFRAME_HEIGHT_PX,
      });
      return false;
    }
    const now = Date.now();
    this.resizeDispatchTimestamps = this.resizeDispatchTimestamps.filter((t) => now - t < RESIZE_BURST_WINDOW_MS);
    this.resizeDispatchTimestamps.push(now);
    if (this.resizeDispatchTimestamps.length > RESIZE_BURST_THRESHOLD) {
      this.resizeDispatchFrozen = true;
      datadogLogs.logger.warn('Embedded iframe resize dispatch frozen: burst threshold exceeded', {
        threshold: RESIZE_BURST_THRESHOLD,
        windowMs: RESIZE_BURST_WINDOW_MS,
      });
      return false;
    }
    return true;
  };

  @action
  embeddedEventDispatcher = (event: string, payload?: Record<string, JsonValue>): boolean => {
    if (window.parent === window) {
      return true;
    }

    if (event.startsWith('iframe:resize') && payload && 'height' in payload) {
      if (!this.shouldDispatchResize(payload.height)) {
        return false;
      }
    }

    window.parent.postMessage(JSON.stringify({ event, payload }), '*');
    return true;
  };

  // navigation
  @action
  showMenu(dispensaryId: string, replace?: boolean): Promise<boolean> | undefined {
    if (this.isMobileEcommApp) {
      changeNativeTabToShop();
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return undefined;
    }
    if (replace) {
      return Router.replace(`/${this.dispensaryRootUrl}/[cName]`, `/${this.dispensaryRootUrl}/${dispensaryId}`);
    }
    return Router.push(`/${this.dispensaryRootUrl}/[cName]`, `/${this.dispensaryRootUrl}/${dispensaryId}`);
  }

  @action
  showOrder(orderId: string, replace?: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    const { ORDERS, ORDER } = ROUTES;
    if (replace) {
      void Router.replace(ORDER, `${ORDERS}/${orderId}?view=status`);
    } else {
      void Router.push(ORDER, `${ORDERS}/${orderId}?view=status`);
    }
  }

  @action
  showPreferences(replace?: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    const { PREFERENCES } = ROUTES;
    if (replace) {
      void Router.replace(PREFERENCES);
    } else {
      void Router.push(PREFERENCES);
    }
  }

  @action
  showHome(replace?: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    if (replace) {
      void Router.replace('/');
    } else {
      void Router.push('/');
    }
  }

  @action
  showShop(replace?: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    const { SHOP } = ROUTES;
    if (replace) {
      void Router.replace(SHOP);
    } else {
      void Router.push(SHOP);
    }
  }

  @action
  redirectToKioskLockScreen(dispensaryCname: string): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    void Router.replace(
      `/${this.dispensaryRootUrl}/[cName]/lock`,
      `/${this.dispensaryRootUrl}/${dispensaryCname}/lock`
    );
  }

  @action.bound
  goBack(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!Router) {
      // Router is null during Next.js Automatic Static Optimization — removing this guard will cause build failures
      return;
    }
    Router.back();
  }

  @action.bound
  isVariant(variant: string[] | string): boolean {
    if (_.isArray(variant)) {
      return _.includes(variant, this.variant);
    }
    return this.variant === variant;
  }

  @action.bound
  showResidentialStatusModal(): Promise<void> | undefined {
    // Here for v2 store compatibility
    if (this.residentialStatus !== 'VERIFIED' || this.currentAddress?.residentialStatus !== 'VERIFIED') {
      return new Promise((resolve, reject) => {
        this.showResidentialVerificationModal = { open: true, resolve, reject };
      });
    }
    return undefined;
  }

  // used in shared checkout
  @action.bound
  hasDeliveryAddress({ location = this.currentAddress }: { location?: CurrentAddress | null } = {}): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const addressLine1 = location?.ln1 || '';

    const streetNumber = !_.isEmpty(_.trim(addressLine1)) && _.split(addressLine1, ' ')[0];
    const streetNumberValidationRegex = /^\d+-?\d*$/;
    const hasValidStreetNumber = streetNumber && streetNumberValidationRegex.test(streetNumber);

    const hasLocationLng = location?.lng;

    const noValidDeliveryAddress = !hasLocationLng || !hasValidStreetNumber;

    if (noValidDeliveryAddress) {
      return false;
    }

    return true;
  }

  @action.bound
  resetAddress(): void {
    this.currentAddress = {};
  }

  @action
  setLoadingOrderType(loading: boolean | undefined): void {
    this.loadingOrderType = !!loading;
  }

  @action
  setCurrentMenuDrawerTab(tab: number): void {
    this.currentMenuDrawerTab = tab;
  }

  @action
  setMenuLayout(layoutType = `cards`): void {
    this.menuLayout = layoutType;
  }

  @action
  setProductPosition(newEntry: ProductPosition | null): void {
    this.productPosition = newEntry;
  }

  @action.bound
  setProductCount(count: number): void {
    this.productCount = count;
    setPersistedValue(`productCount`, count);
  }

  @action
  setViewMode(query?: { preview?: string; viewOnlyMode?: string }): void {
    this.previewMode = query?.preview === 'true';
    this.viewOnlyMode = query?.viewOnlyMode === 'true';
  }

  @action
  setPath(href: string, route: string): void {
    this.href = href;
    this.route = route;
  }

  @action updateAddress({ location, residentialStatus = 'UNVERIFIED' }: UpdateAddressProps): void {
    if (location.geometry?.coordinates) {
      const [lng, lat] = location.geometry.coordinates;
      location = { ...location, lng, lat };
    }
    this.currentAddress = { ...location, residentialStatus };
    this.tempLocation = { ...location, residentialStatus };
    this.tempAddress = this.formattedAddressWithoutApt;
    if (!_.isEmpty(location.ln2) && location.ln2 != null) {
      this.apt = location.ln2;
    } else if (location.apt) {
      this.apt = location.apt;
    }
  }

  @action setHideLogin(value: boolean): void {
    this.hideLogin = value;
  }

  @computed
  get dispensaryRootUrl(): string {
    if (this.isDemo) {
      return `demo`;
    }
    return {
      default: `dispensary`,
      'store-front': `stores`,
      embedded: `embedded-menu`,
      kiosk: `kiosks`,
      'mobile-ecomm': `mobile-ecomm`,
    }[this.variant];
  }

  @computed
  get isMobileEcommApp(): boolean {
    return _.startsWith(this.route, '/mobile-ecomm/');
  }

  @computed
  get isConciergeActive(): boolean {
    // Concierge route shape is `/stores/{cname}/concierge`. Next.js `route` is
    // the pathname only (no query / hash), so endsWith is safe + precise.
    return _.endsWith(this.route, '/concierge');
  }

  @computed
  get isCartPage(): boolean {
    return _.includes(this.route, '/cart');
  }

  @computed
  get isProductDetailPage(): boolean {
    return _.includes(this.route, ROUTES.PRODUCT_ID);
  }

  @computed
  get isMessaging(): boolean {
    return _.startsWith(this.route, '/messaging/');
  }

  // Core menu variant checks
  // we use href instead of route for these bc route is changed to /_error in the case of a bad path
  // we need an accurate variant in this case to serve the right layout on the whoops page
  @computed
  get isKiosk(): boolean {
    return _.startsWith(this.href, '/kiosks/');
  }

  @computed
  get isEmbedded(): boolean {
    if (_.startsWith(this.href, '/embedded-menu/')) {
      return true;
    }
    if (this.isIframe) {
      return true;
    }
    return false;
  }

  @computed
  get isCMS(): boolean {
    return _.startsWith(this.href, '/cms/');
  }

  @computed
  get isStoreFront(): boolean {
    return this.isCMS || _.startsWith(this.href, '/stores/');
  }
  // end core menu variant checks

  @computed
  get onBrandsPage(): boolean {
    return _.includes(this.route, ROUTES.BRANDS);
  }

  @computed
  get onSearchPage(): boolean {
    return _.endsWith(this.route, ROUTES.ALL_PRODUCTS_SEARCH);
  }

  @computed
  get onCollectionsPage(): boolean {
    return _.includes(this.route, ROUTES.COLLECTIONS);
  }

  @computed
  get onSubcategoryPage(): boolean {
    return _.endsWith(this.route, ROUTES.SUBCATEGORY);
  }

  @computed
  get analyticsMenuVariant(): string {
    return this.isDutchieMain ? `main` : this.variant;
  }

  @computed
  get isEmbeddedCarousel(): boolean {
    return this.route.indexOf('/carousels/') > 0;
  }

  @computed
  get isDemo(): boolean {
    return _.startsWith(this.route, '/demo/');
  }

  @computed
  get isPlus(): boolean {
    return _.startsWith(this.route, '/checkouts/');
  }

  @computed
  get isMarketplace(): boolean {
    return _.startsWith(this.route, ROUTES.SHOP);
  }

  @computed
  get isMarketplaceDispensary(): boolean {
    return _.startsWith(this.route, ROUTES.SHOP_DISPENSARY);
  }

  @computed
  get isAccountPage(): boolean {
    return _.includes(this.route, `/user/[accountTab]`);
  }

  @computed
  get isSpecialPage(): boolean {
    return _.endsWith(this.route, ROUTES.SPECIAL_ID);
  }

  @computed
  get isLocationSelectorPage(): boolean {
    return _.includes(this.route, '/locations');
  }

  @computed
  get isNewsPage(): boolean {
    return _.startsWith(this.route, '/news');
  }

  @computed
  get isOnboardingPage(): boolean {
    return _.includes(this.route, '/getstarted');
  }

  @computed
  get isDutchieMain(): boolean {
    return this.variant === InterfaceVariants.dutchieMain;
  }

  @computed
  get isCheckoutPage(): boolean {
    return _.includes(this.route, '/checkout');
  }

  @computed
  get variant(): InterfaceVariants {
    if (this.isPlus) {
      return InterfaceVariants.plus;
    }

    if (this.isStoreFront) {
      return InterfaceVariants.storeFront;
    }

    // URL-based checks (isKiosk) must precede iframe-based checks (isEmbedded)
    // because a kiosk page loaded in an iframe (e.g. reverse-proxied kiosk menus)
    // should resolve as kiosk, not embedded. See ECOMM-7763.
    if (this.isKiosk) {
      return InterfaceVariants.kiosk;
    }

    if (this.isEmbedded) {
      return InterfaceVariants.embedded;
    }

    if (this.isMessaging) {
      return InterfaceVariants.messaging;
    }

    if (this.isDemo) {
      return InterfaceVariants.embedded;
    }

    if (this.isMobileEcommApp) {
      return InterfaceVariants.mobileEcomm;
    }

    return InterfaceVariants.dutchieMain;
  }

  @computed
  get hasAddress(): boolean {
    return !!this.currentAddress?.zipcode;
  }

  @computed
  get formattedAddress(): string {
    if (!this.hasAddress) {
      return '';
    }
    const { ln1, ln2, city, state, zipcode, country } = this.currentAddress ?? {};
    return formatAddress({ ln1, ln2, city, state, zipcode, country });
  }

  @computed
  get formattedAddressWithoutApt(): string {
    if (!this.hasAddress) {
      return '';
    }
    const { ln1, city, state, zipcode, country } = this.currentAddress ?? {};
    return formatAddressWithoutApt({ ln1, city, state, zipcode, country });
  }

  @computed
  get formattedAddressForCheckout(): string {
    if (!this.hasAddress) {
      return '';
    }
    const { ln1, ln2 } = this.currentAddress ?? {};
    return formatAddressForCheckout({ ln1, ln2 });
  }

  @computed
  get address(): string {
    return this.formattedAddressWithoutApt;
  }

  @computed
  get globalHeaderHeight(): number | null | undefined {
    const { DISPENSARY_CNAME, DISPENSARIES } = ROUTES;
    return _.some([DISPENSARY_CNAME, DISPENSARIES], (routesWithGlobalHeader) =>
      _.includes(this.route, routesWithGlobalHeader)
    )
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        document?.querySelector<HTMLElement>(`#global-header`)?.offsetHeight
      : null;
  }

  @computed
  get dispensaryHeaderHeight(): number | null | undefined {
    const { DISPENSARY_CNAME } = ROUTES;
    return _.includes(this.route, DISPENSARY_CNAME)
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        document?.querySelector<HTMLElement>(`#dispensary-header`)?.offsetHeight
      : null;
  }
}
